import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Éléments communs aux éditeurs de chiffrage et de proposition.

export const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Flèche de retour : vers la page d'où l'on vient (dossier du projet, liste, mail…), libellée par la page qui a
// ouvert l'éditeur (state.backLabel) ; ouvert directement par son adresse, vers la liste du type de document.
export const BackLink = ({ to, label }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const className = "inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800";
  if (location.key === "default") {
    return <Link to={to} className={className}><ArrowLeft className="h-4 w-4" /> {label}</Link>;
  }
  return (
    <button type="button" onClick={() => navigate(-1)} className={className}>
      <ArrowLeft className="h-4 w-4" /> {location.state?.backLabel || "Retour"}
    </button>
  );
};

export const STATUS_LABEL = {
  saved: "Enregistré",
  dirty: "Modifications en attente…",
  saving: "Enregistrement…",
  error: "Échec de l'enregistrement",
  conflict: "Conflit de version",
};

export const TitleField = ({ value, editable, onCommit, label }) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  if (!editable) return <h1 className="text-2xl font-semibold text-slate-900">{value}</h1>;
  const commit = () => {
    const next = text.trim();
    if (next && next !== value) onCommit(next);
    else setText(value);
  };
  return (
    <input
      aria-label={label}
      data-own-undo
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
      className="w-full rounded-lg border border-transparent bg-transparent px-1 text-2xl font-semibold text-slate-900 hover:border-slate-200 focus:border-blue-400 focus:outline-none"
    />
  );
};

export const ToolbarButton = ({ onClick, disabled, title, children, active }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition disabled:opacity-40 ${
      active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
    }`}
  >
    {children}
  </button>
);

export const SaveStatus = ({ doc }) => {
  if (!doc.canEdit || doc.archived) return null;
  const { status, data } = doc;
  return (
    <span className={`text-xs ${status === "error" || status === "conflict" ? "text-red-600" : "text-slate-400"}`}>
      {STATUS_LABEL[status]}{status === "saved" && data?.revision ? ` · v${data.revision.number}` : ""}
    </span>
  );
};

// Bandeaux de synchronisation : conflit, erreur, message, consultation d'une ancienne version.
export const SyncBanners = ({ doc, noun }) => {
  const { status, message, setMessage, save, loadLatest, keepMine, viewing, setViewing, restoreRevision, canEdit, archived } = doc;
  return (
    <>
      {status === "conflict" && (
        <div className="flex flex-col gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between">
          <span>{noun} a été modifié ailleurs pendant ta saisie.</span>
          <div className="flex gap-2">
            <button type="button" onClick={loadLatest} className="rounded-full bg-white px-3 py-1 text-slate-800 shadow-sm">Charger la dernière version</button>
            <button type="button" onClick={keepMine} className="rounded-full bg-red-700 px-3 py-1 text-white">Garder mes modifications</button>
          </div>
        </div>
      )}
      {status === "error" && message && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {message} <button type="button" onClick={save} className="ml-2 underline">Réessayer</button>
        </div>
      )}
      {status !== "error" && message && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage("")} className="text-xs underline">Fermer</button>
        </div>
      )}
      {viewing && (
        <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Tu consultes la version {viewing.revision.number}
            {viewing.revision.label ? ` « ${viewing.revision.label} »` : ""} du {formatDate(viewing.revision.updated_at)}.
          </span>
          <div className="flex gap-2">
            {canEdit && !archived && (
              <button type="button" onClick={() => restoreRevision(viewing.revision.id)} className="rounded-full bg-blue-700 px-3 py-1 text-white">
                Restaurer cette version
              </button>
            )}
            <button type="button" onClick={() => setViewing(null)} className="rounded-full bg-white px-3 py-1 text-slate-800 shadow-sm">
              Revenir à la version actuelle
            </button>
          </div>
        </div>
      )}
    </>
  );
};
